import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Popover, PopoverAnchor, PopoverContent } from '@roger/ui-components';
import type { EpisodeLupaPeriodResponse } from '../services/api';
import type { TaskListResponse } from '../services/api/types.gen';
import { parseISOToLocalDate, formatDateToMMDDYYYY } from '../utils/dateUtils';
import {
  getLupaContributingVisits,
  type LupaVisitContributionKind,
} from '../utils/lupaVisitUtils';
import { getStatusLabel } from './task/taskStatus';

type LupaPeriodStatus = EpisodeLupaPeriodResponse['status'];
// `missing_inputs` is optional in the generated schema, so index its non-null
// form to get the element type.
type MissingInput = NonNullable<
  EpisodeLupaPeriodResponse['missing_inputs']
>[number];

interface LupaThresholdGaugeProps {
  periods: EpisodeLupaPeriodResponse[];
  /** Episode visit tasks for the contributing-visits popover (optional). */
  visits?: TaskListResponse[];
  /**
   * Billable visits not yet created, by period number (Schedule Visits).
   * Added to the scheduled count and shown as a lighter bar segment.
   */
  newVisitCounts?: ReadonlyMap<number, number>;
  /** Lays out the period blocks; a vertical stack by default. */
  className?: string;
}

const ORDINALS = ['1st', '2nd', '3rd', '4th', '5th'];

/**
 * "1st period". Not "1st 30-day period": under a heading that already says
 * LUPA thresholds, every period is a 30-day one, and the four words pushed the
 * row's chips and count onto a second line beside the calendar.
 */
const periodName = (periodNumber: number): string =>
  `${ORDINALS[periodNumber - 1] ?? `${periodNumber}th`} period`;

/**
 * The hatched fill for the bar's uncertainty band — the span between the lowest
 * and highest threshold the period could still land on.
 */
const HATCH_FILL =
  'bg-[repeating-linear-gradient(45deg,rgba(107,114,128,0.35)_0_3px,transparent_3px_6px)]';

// No dedicated success/warning token in the design system, so status colors use
// the Tailwind palette for clear/at-risk and the --destructive token for LUPA.
// Static class strings (no interpolation) so Tailwind keeps them.
//
// Status color rides on the bar and its status line only. The card tints these
// used to carry were dropped with the cards: beside the calendar the
// strip has to read as a light list of bars, and a full-width tint per period
// is the heaviest thing on the page.
const STATUS_STYLE: Record<LupaPeriodStatus, { bar: string; accent: string }> =
  {
    clear: { bar: 'bg-emerald-600', accent: 'text-emerald-700' },
    at_risk: { bar: 'bg-amber-500', accent: 'text-amber-700' },
    lupa: { bar: 'bg-destructive', accent: 'text-destructive' },
    unknown: { bar: 'bg-gray-300', accent: 'text-gray-700' },
  };

/**
 * Why a period's value is not settled yet, most upstream first. The first match
 * is the one shown: a period with no diagnoses coded is not usefully described
 * as "waiting on signature".
 */
const MISSING_INPUT_PRIORITY: MissingInput[] = [
  // Nothing the user enters can fix it, so it outranks every data gap.
  'unsupported_grouper_version',
  'primary_diagnosis',
  'oasis_assessment',
  'oasis_answers',
  'diagnosis_coding',
  'signature',
];

const MISSING_INPUT_LABEL: Record<MissingInput, string> = {
  unsupported_grouper_version: 'Grouper version not supported',
  primary_diagnosis: 'No primary diagnosis',
  oasis_assessment: 'No OASIS assessment',
  oasis_answers: 'OASIS not finished',
  diagnosis_coding: 'Diagnoses can still change',
  signature: 'Waiting on signature',
};

const primaryMissingInput = (
  period: EpisodeLupaPeriodResponse
): MissingInput | null => {
  // Optional in the generated schema, so treat an absent list as "nothing known
  // to be outstanding" rather than throwing on a payload without it.
  const missing = period.missing_inputs ?? [];
  return MISSING_INPUT_PRIORITY.find(cause => missing.includes(cause)) ?? null;
};

/** The OASIS items with no response yet, absent list treated as none. */
const unansweredItems = (period: EpisodeLupaPeriodResponse): string[] =>
  period.unanswered_items ?? [];

/**
 * The parenthetical after the period's name ("1st 30-day period (No primary
 * diagnosis)"). Estimated states name themselves so the number beside them is
 * never read as settled; a final period says nothing at all.
 *
 * Always a short bucket phrase, never the backend's `detail`. `detail` is
 * free text the grouper wrote — an M1033 conflict runs to a couple of hundred
 * characters — and this label ends up in a `whitespace-nowrap` chip inside a
 * fixed-width column, where a sentence overflows the page instead of wrapping.
 * The reason in the backend's own words is still shown; it lives in the
 * popover, which has the room for it.
 */
const causeLabel = (period: EpisodeLupaPeriodResponse): string | null => {
  if (period.certainty === 'final') {
    return null;
  }
  const cause = primaryMissingInput(period);
  if (!cause) {
    // An unavailable period with no named cause is the estimate pipeline
    // failing, not a data gap the user can close: the schema service is down,
    // the episode has no admission row, the grouper refused (see
    // `_safe_period_estimate`, which withholds a cause on purpose rather than
    // name the wrong one). That row has no threshold and so no count either,
    // which left it rendering as a period name over an empty track — nothing
    // at all to tell a scheduler the blank is the system's problem. Vague
    // because the backend genuinely cannot say more here — but it reads as a
    // fault rather than as a wait, so it is not mistaken for a period whose
    // inputs simply have not landed yet.
    return period.certainty === 'unavailable' ? "Couldn't calculate" : null;
  }
  if (cause === 'primary_diagnosis' && period.detail) {
    // A `primary_diagnosis` cause covers two different situations, and the
    // bucket label only fits one of them. The backend also files "CMS refuses
    // to group the coded principal" here (`UngroupablePrincipalDiagnosisError`
    // in `hipps_estimate.py`), and it always attaches a `detail` when it does.
    // "No primary diagnosis" would send a scheduler hunting for a diagnosis
    // that is already coded, so name the actual problem instead. Still a short
    // bucket phrase, not the `detail` itself — see the docblock above.
    return "Diagnosis won't group";
  }
  // The bare reason, with no "Estimated —" / "Not available yet —" prefix: the
  // period already carries an Estimated chip or a `?`-masked HIPPS pattern, and
  // an unavailable period has no number to qualify at all. The
  // prefix was the same word twice on a two-line row.
  return MISSING_INPUT_LABEL[cause];
};

/** "4", or "3–4" when the threshold is known only to within a range. */
const thresholdLabel = (period: EpisodeLupaPeriodResponse): string | null => {
  const { lupa_threshold_low: low, lupa_threshold_high: high } = period;
  if (low == null || high == null) {
    return null;
  }
  return low === high ? `${low}` : `${low}–${high}`;
};

/**
 * CMS publishes case-mix weights to four decimals, and the product already
 * shows them that way on the coding page's Payout Weight card. Same number,
 * same shape, so a coder moving between the two screens reads one value.
 */
const formatWeight = (weight: number): string => weight.toFixed(4);

/**
 * "×0.9973", or "×0.8689–1.0804" while the code can still move.
 *
 * The `×` is doing real work: the weight is a multiplier on CMS's standard
 * 30-day rate, and the glyph says so before the popover's sentence spells it
 * out. It also keeps the number from reading as a sixth HIPPS position.
 *
 * Ranges are the common case here, not the exception. CMS prices 432 codes
 * with 4 distinct LUPA thresholds but 412 distinct weights, so a period whose
 * positions are still open will nearly always report a span even when its
 * threshold has already settled to one number.
 */
const weightLabel = (period: EpisodeLupaPeriodResponse): string | null => {
  const { case_mix_weight_low: low, case_mix_weight_high: high } = period;
  if (low == null || high == null) {
    return null;
  }
  return low === high
    ? `×${formatWeight(low)}`
    : `×${formatWeight(low)}–${formatWeight(high)}`;
};

/**
 * Descriptive, never imperative. This gauge is read by schedulers, and a line
 * telling them to add N visits to reach a payment threshold reads as an
 * instruction to schedule for payment rather than for clinical need. The visit
 * count and the threshold carry the same information without saying that.
 */
const scheduledStatusLabel = (
  period: EpisodeLupaPeriodResponse,
  status: LupaPeriodStatus,
  newCount: number
): string | null => {
  const threshold = thresholdLabel(period);
  if (threshold == null) {
    // Nothing to say: with no threshold there is no count to place against it,
    // and the cause line below names what is missing.
    return null;
  }
  // The count carries the state on its own against the threshold beside it, so
  // only the two ends are named: "met", and the closed-and-short
  // "LUPA" that changes how the period is paid. An open period short of its
  // threshold is just its count — the amber bar already says it is not there
  // yet, and "below threshold" restated the two numbers in words.
  //
  // "scheduled" because the count includes visits that have not happened yet
  // (missed visits excluded), the same figure WellSky shows as "Billable Visits
  // Scheduled". Without it, "4 of 2 · met" read as four visits delivered.
  const scheduled =
    newCount > 0
      ? `${period.billable_visit_count} scheduled + ${newCount} new`
      : `${period.billable_visit_count} scheduled`;
  const counts = `${scheduled} of ${threshold}`;
  if (status === 'clear') {
    return `${counts} · met`;
  }
  if (status === 'lupa') {
    return `${counts} · LUPA`;
  }
  return counts;
};

/**
 * The status a period would have with `newCount` more scheduled visits. New
 * visits only raise the count: met at the high end, and a closed LUPA back to
 * at risk at the low end, the same asymmetric reading the server applies.
 */
const projectedStatus = (
  period: EpisodeLupaPeriodResponse,
  newCount: number
): LupaPeriodStatus => {
  if (newCount === 0 || period.status === 'unknown') {
    return period.status;
  }
  const total = period.billable_visit_count + newCount;
  const { lupa_threshold_low: low, lupa_threshold_high: high } = period;
  if (high != null && total >= high) {
    return 'clear';
  }
  if (period.status === 'lupa' && low != null && total >= low) {
    return 'at_risk';
  }
  return period.status;
};

/**
 * Guaranteed / already-carried-out line. Uses submitted_billable_visit_count;
 * "met" only when submitted clears the high threshold — period.status still
 * follows the scheduled count.
 */
const submittedStatusLabel = (
  period: EpisodeLupaPeriodResponse
): string | null => {
  const threshold = thresholdLabel(period);
  if (threshold == null) {
    return null;
  }
  const submitted = period.submitted_billable_visit_count ?? 0;
  const counts = `${submitted} submitted of ${threshold}`;
  const high = period.lupa_threshold_high;
  if (high != null && submitted >= high) {
    return `${counts} · met`;
  }
  return counts;
};

/**
 * The `?` positions in a HIPPS pattern, named in the words a scheduler would
 * use to find the unanswered thing. Pattern index 1 (the clinical group) is
 * left out on purpose: it is set by the primary diagnosis, which index 3's
 * "diagnosis coding" already sends the reader to, and naming it twice would
 * read as two separate gaps.
 */
const STILL_OPEN_BY_POSITION: { index: number; label: string }[] = [
  {
    index: 0,
    label: 'How the patient was admitted (inpatient-discharge questions)',
  },
  { index: 2, label: 'Functional ability questions' },
  { index: 3, label: 'Diagnosis coding' },
];

const stillOpenItems = (period: EpisodeLupaPeriodResponse): string[] => {
  const pattern = period.hipps_pattern;
  if (!pattern) {
    return [];
  }
  return STILL_OPEN_BY_POSITION.filter(
    ({ index }) => pattern[index] === '?'
  ).map(({ label }) => label);
};

/**
 * What the counts already settle, even while the threshold does not. The
 * takeaway of the popover, and the only line in it set in a darker weight.
 *
 * Descriptive arithmetic only — the same rule the card's status line follows.
 * "This period does not yet have enough visits" is a statement about the
 * period; "schedule three more visits" would be an instruction to schedule for
 * payment.
 */
const outcomeSentence = (
  period: EpisodeLupaPeriodResponse,
  newCount: number
): string | null => {
  const { lupa_threshold_low: low, lupa_threshold_high: high } = period;
  const count = period.billable_visit_count + newCount;
  const visits =
    newCount > 0
      ? `${period.billable_visit_count} scheduled and ${newCount} new visits`
      : `${count} scheduled visits`;
  if (high != null && high > 0 && count >= high) {
    return `This period has ${visits}. That clears every possible threshold.`;
  }
  if (low != null && count < low) {
    return newCount > 0
      ? 'With the new visits, this period does not yet have enough for any possible threshold.'
      : 'This period does not yet have enough scheduled visits for any possible threshold.';
  }
  return null;
};

const fillPercent = (count: number, high: number): number =>
  high > 0 ? Math.min(100, Math.round((count / high) * 100)) : 0;

/**
 * Controlled popover that opens on hover (desktop) and click (touch).
 * Uses PopoverAnchor instead of Trigger so a click after hover does not
 * toggle closed — Radix Trigger's toggle fights mouseenter in one gesture.
 * Do not gate on `(hover: hover)` — some desktop shells report it as false.
 *
 * Touch trade-off (same as LongTextPreview): with Anchor, a pointer-down on
 * the chip counts as outside, so a second tap can close and immediately
 * reopen. A plain open/close toggle breaks the first desktop tap
 * (mouseenter fires before click). Leave a shared hover/click hook for later
 * rather than paper over it here.
 */
const useHoverOpenPopover = () => {
  const [open, setOpen] = useState(false);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current != null) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const openNow = useCallback(() => {
    clearCloseTimer();
    setOpen(true);
  }, [clearCloseTimer]);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimer.current = setTimeout(() => setOpen(false), 150);
  }, [clearCloseTimer]);

  useEffect(() => () => clearCloseTimer(), [clearCloseTimer]);

  return {
    open,
    onOpenChange: (next: boolean) => {
      clearCloseTimer();
      setOpen(next);
    },
    openNow,
    scheduleClose,
  };
};

const HoverOpenPopover: React.FC<{
  trigger: React.ReactElement;
  contentProps: React.ComponentProps<typeof PopoverContent> & {
    'data-testid'?: string;
  };
  children: React.ReactNode;
}> = ({ trigger, contentProps, children }) => {
  const { open, onOpenChange, openNow, scheduleClose } = useHoverOpenPopover();
  const { onMouseEnter, onMouseLeave, ...restContentProps } = contentProps;
  const anchorRef = React.useRef<HTMLElement>(null);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      {/* An Anchor, unlike a Trigger, does not announce the popover, so the
          expanded state is set on the trigger here. */}
      <PopoverAnchor asChild>
        {React.cloneElement(trigger, {
          ref: anchorRef,
          'aria-haspopup': 'dialog',
          'aria-expanded': open,
          onMouseEnter: openNow,
          onMouseLeave: scheduleClose,
          onClick: openNow,
        })}
      </PopoverAnchor>
      <PopoverContent
        // A hover-opened panel must not pull focus off whatever the user was
        // typing in (same as LongTextPreview).
        onOpenAutoFocus={event => event.preventDefault()}
        // A click on the anchor is not an outside interaction: onClick
        // reopens it, so dismissing here would flicker the panel closed.
        onInteractOutside={event => {
          if (anchorRef.current?.contains(event.target as Node)) {
            event.preventDefault();
          }
        }}
        {...restContentProps}
        onMouseEnter={event => {
          openNow();
          onMouseEnter?.(event);
        }}
        onMouseLeave={event => {
          scheduleClose();
          onMouseLeave?.(event);
        }}
      >
        {children}
      </PopoverContent>
    </Popover>
  );
};

const ContributingVisitsPopover: React.FC<{
  period: EpisodeLupaPeriodResponse;
  visits: TaskListResponse[];
  kind: LupaVisitContributionKind;
  label: string;
  accentClass: string;
}> = ({ period, visits, kind, label, accentClass }) => {
  const windowStart = parseISOToLocalDate(period.start_date);
  const windowEnd = parseISOToLocalDate(period.end_date);
  const contributing =
    windowStart && windowEnd
      ? getLupaContributingVisits(visits, windowStart, windowEnd, kind)
      : [];

  return (
    <HoverOpenPopover
      trigger={
        <button
          type='button'
          aria-label={`${label} — contributing visits`}
          className={`whitespace-nowrap text-xs underline decoration-dotted underline-offset-2 transition-colors hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-1 ${accentClass}`}
        >
          {label}
        </button>
      }
      contentProps={{
        align: 'end',
        'data-testid': `lupa-visits-${kind}-${period.period_number}`,
        className:
          'w-80 max-w-[calc(100vw-2rem)] space-y-2 p-3 text-xs font-normal normal-case leading-relaxed tracking-normal text-gray-600',
      }}
    >
      <p className='font-medium text-gray-700'>
        {kind === 'scheduled' ? 'Scheduled' : 'Submitted'} billable visits
      </p>
      {contributing.length === 0 ? (
        <p>No billable visits in this count yet.</p>
      ) : (
        <ul className='max-h-56 space-y-1.5 overflow-y-auto'>
          {contributing.map(visit => {
            // Scheduled lists plot by target date; submitted lists the service
            // date that had to land in-window for the visit to count.
            const listDate = parseISOToLocalDate(
              kind === 'submitted'
                ? (visit.task_date ?? visit.scheduled_date)
                : visit.scheduled_date
            );
            return (
              <li
                key={visit.id}
                className='flex flex-col gap-0.5 border-b border-gray-100 pb-1.5 last:border-0 last:pb-0'
              >
                <span className='font-medium text-gray-800'>
                  {visit.display_name || 'Visit'}
                </span>
                <span className='text-gray-500'>
                  {listDate ? formatDateToMMDDYYYY(listDate) : '—'}
                  {' · '}
                  {getStatusLabel(visit.status)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </HoverOpenPopover>
  );
};

/**
 * Per-30-day-period LUPA gauge for the episode-manager calendar.
 *
 * Presentational. Each period is one block: the period name and its HIPPS on
 * the left, then a scheduled count/bar and a quieter submitted count/bar.
 * Narrowed to fit a 224px column (the panel's `xl:w-56` strip) beside the
 * calendar.
 *
 * Uncertainty is carried by three things instead, so it is legible before any
 * word is read: the HIPPS is masked (`3H??1`) where digits can still move, a
 * hatched band covers the span between the lowest and highest possible
 * threshold, and a dashed chip names the state ("Estimated", or the cause
 * itself when there is no threshold to qualify).
 *
 * The HIPPS sits beside the period name rather than in the popover: it is the
 * code everything else on the row follows from, and a popover holding only a
 * copy of something already visible is an empty popover with extra steps. The
 * case-mix weight lives *behind* the code, in a popover the code itself opens.
 *
 * The gauge always renders, including when no threshold is determinable. An
 * empty space asks a question the user cannot answer; a named cause answers it.
 *
 * It stays in the same descriptive register as the card: it explains the
 * arithmetic, never prescribes a visit.
 */
const LupaThresholdGauge: React.FC<LupaThresholdGaugeProps> = ({
  periods,
  visits = [],
  newVisitCounts,
  className = 'space-y-3',
}) => {
  if (periods.length === 0) {
    return null;
  }

  const canListVisits = visits.length > 0;

  return (
    <div className={className}>
      {periods.map(period => {
        const newCount = newVisitCounts?.get(period.period_number) ?? 0;
        const status = projectedStatus(period, newCount);
        const style = STATUS_STYLE[status];
        const hippsLabel = period.hipps_code ?? period.hipps_pattern;
        const weight = weightLabel(period);

        // The bar is scaled to the highest possible threshold, so a period can
        // only look complete once it clears every candidate.
        const high = period.lupa_threshold_high ?? 0;
        const low = period.lupa_threshold_low ?? high;
        const scheduledFillPct = fillPercent(period.billable_visit_count, high);
        const newFillPct = fillPercent(
          period.billable_visit_count + newCount,
          high
        );
        const submittedCount = period.submitted_billable_visit_count ?? 0;
        const submittedFillPct = fillPercent(submittedCount, high);
        // Where the uncertain part of the target begins.
        const bandStartPct = high > 0 ? Math.round((low / high) * 100) : 100;
        const hasRange = high > 0 && low !== high;
        const scheduledStatus = scheduledStatusLabel(period, status, newCount);
        const submittedStatus = submittedStatusLabel(period);
        const cause = causeLabel(period);

        const stillOpen =
          period.certainty === 'estimated' ? stillOpenItems(period) : [];
        const outcome =
          period.certainty === 'estimated'
            ? outcomeSentence(period, newCount)
            : null;
        const unanswered = unansweredItems(period);
        // The one mark a row carries besides its count, and the thing you
        // click for the rest. A settled period has none: its number
        // is the whole story. Everything else is "Estimated", except a period
        // with no number at all, where the chip is the reason there isn't one
        // — the chip is the row's state, whatever that state is, so there is
        // never a second differently-shaped affordance beside it.
        const chipLabel =
          period.certainty === 'final'
            ? null
            : scheduledStatus == null
              ? cause
              : 'Estimated';
        // Said once: on the chip, or in the popover, never both.
        const popoverCause = chipLabel === cause ? null : cause;
        // A chip only becomes a button when there is something behind it. A
        // period whose chip already is its whole explanation stays inert
        // rather than opening an empty card.
        // The HIPPS is no longer among these: it is on the row itself now, and
        // a popover holding only a copy of something already visible is an
        // empty popover with extra steps.
        const hasPopoverContent =
          popoverCause != null ||
          stillOpen.length > 0 ||
          outcome != null ||
          unanswered.length > 0 ||
          !!period.detail;

        const submittedMet =
          high > 0 && submittedCount >= high
            ? 'text-emerald-700'
            : 'text-gray-600';

        const scheduledCountNode = scheduledStatus ? (
          canListVisits ? (
            <ContributingVisitsPopover
              period={period}
              visits={visits}
              kind='scheduled'
              label={scheduledStatus}
              accentClass={style.accent}
            />
          ) : (
            <span className={`whitespace-nowrap text-xs ${style.accent}`}>
              {scheduledStatus}
            </span>
          )
        ) : null;

        return (
          <div key={period.period_number} data-certainty={period.certainty}>
            {/* Header matches pre-submitted layout: period + HIPPS (+ chip) on
                the left, scheduled count on the right. */}
            <div className='mb-1 flex flex-wrap items-center justify-between gap-x-2 gap-y-1'>
              <span className='flex flex-wrap items-center gap-1.5 text-xs font-medium text-gray-600'>
                {periodName(period.period_number)}
                {hippsLabel && !weight && (
                  <span className='font-mono'>{hippsLabel}</span>
                )}
                {hippsLabel && weight && (
                  <HoverOpenPopover
                    trigger={
                      <button
                        type='button'
                        aria-label={`HIPPS ${hippsLabel} — case-mix weight`}
                        className='font-mono text-gray-600 underline decoration-gray-400 decoration-dotted underline-offset-2 transition-colors hover:text-gray-800 hover:decoration-gray-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-1'
                      >
                        {hippsLabel}
                      </button>
                    }
                    contentProps={{
                      align: 'start',
                      'data-testid': `hipps-weight-${period.period_number}`,
                      className:
                        'w-72 max-w-[calc(100vw-2rem)] space-y-1 p-3 text-xs font-normal normal-case leading-relaxed tracking-normal text-gray-600',
                    }}
                  >
                    <p className='font-medium text-gray-700'>
                      Case-mix weight{' '}
                      <span className='font-mono'>{weight}</span>
                    </p>
                  </HoverOpenPopover>
                )}
                {chipLabel && !hasPopoverContent && (
                  <span className='whitespace-nowrap rounded-full border border-dashed border-gray-400 px-1 py-0 text-[0.65rem] font-semibold leading-4 tracking-normal text-gray-600'>
                    {chipLabel}
                  </span>
                )}
                {chipLabel && hasPopoverContent && (
                  <HoverOpenPopover
                    trigger={
                      <button
                        type='button'
                        aria-label={`${chipLabel} — what this period is waiting on`}
                        className='whitespace-nowrap rounded-full border border-dashed border-gray-400 px-1 py-0 text-[0.65rem] font-semibold leading-4 tracking-normal text-gray-600 transition-colors hover:border-gray-500 hover:bg-gray-50 hover:text-gray-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 focus-visible:ring-offset-1'
                      >
                        {chipLabel}
                      </button>
                    }
                    contentProps={{
                      align: 'start',
                      'data-testid': `lupa-help-${period.period_number}`,
                      className:
                        'w-80 max-w-[calc(100vw-2rem)] space-y-2 p-3 text-xs font-normal normal-case leading-relaxed tracking-normal text-gray-600',
                    }}
                  >
                    {popoverCause && <p>{popoverCause}</p>}
                    {stillOpen.length > 0 && (
                      <div className='space-y-0.5'>
                        <p>Still open:</p>
                        <ul className='list-disc space-y-0.5 pl-4'>
                          {stillOpen.map(item => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {unanswered.length > 0 && (
                      <div className='space-y-0.5'>
                        <p>No response yet:</p>
                        <ul className='list-disc space-y-0.5 pl-4'>
                          {unanswered.map(item => (
                            <li key={item}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {period.detail && (
                      <p className='font-medium text-gray-700'>
                        {period.detail}
                      </p>
                    )}
                    {outcome && (
                      <p className='font-medium text-gray-700'>{outcome}</p>
                    )}
                  </HoverOpenPopover>
                )}
              </span>
              {scheduledCountNode}
            </div>

            {/* Scheduled bar — primary signal (status colors unchanged). */}
            <div className='relative h-1.5 overflow-hidden rounded-full bg-gray-100'>
              {hasRange && (
                <div
                  data-testid={`lupa-range-band-${period.period_number}`}
                  className={`absolute inset-y-0 ${HATCH_FILL}`}
                  style={{
                    left: `${bandStartPct}%`,
                    right: 0,
                  }}
                />
              )}
              <div
                className={`h-full rounded-full ${style.bar}`}
                style={{ width: `${scheduledFillPct}%` }}
              />
              {newFillPct > scheduledFillPct && (
                <div
                  data-testid={`lupa-new-visits-bar-${period.period_number}`}
                  className={`absolute inset-y-0 rounded-full opacity-40 ${style.bar}`}
                  style={{
                    left: `${scheduledFillPct}%`,
                    width: `${newFillPct - scheduledFillPct}%`,
                  }}
                />
              )}
            </div>

            {/* Submitted / guaranteed secondary row — quieter than scheduled. */}
            {submittedStatus && (
              <>
                <div className='mb-0.5 mt-1.5 flex justify-end'>
                  {canListVisits ? (
                    <ContributingVisitsPopover
                      period={period}
                      visits={visits}
                      kind='submitted'
                      label={submittedStatus}
                      accentClass={submittedMet}
                    />
                  ) : (
                    <span
                      className={`whitespace-nowrap text-xs ${submittedMet}`}
                    >
                      {submittedStatus}
                    </span>
                  )}
                </div>
                <div
                  className='relative h-1 overflow-hidden rounded-full bg-gray-100'
                  data-testid={`lupa-submitted-bar-${period.period_number}`}
                >
                  <div
                    className={`h-full rounded-full ${
                      high > 0 && submittedCount >= high
                        ? 'bg-emerald-500/80'
                        : 'bg-slate-400'
                    }`}
                    style={{ width: `${submittedFillPct}%` }}
                  />
                </div>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default LupaThresholdGauge;
